import { IsoDate } from './windows'

export type Trend = {
    current: number
    prior: number
    deltaAbs: number
    deltaPct: number | null
    significanceSd: number | null
    steady: boolean
}
export type RateTrend = {
    current: number | null
    prior: number | null
    deltaPts: number | null
    significanceSd: number | null
    steady: boolean
    baselineMean: number | null
}

const STEADY_BELOW_SD = 1
const OUTLIER_MAD_MULTIPLE = 2.5
const FLOATING_POINT_NOISE = 1e-12

const sum = (values: number[]) => values.reduce((total, value) => total + value, 0)
const mean = (values: number[]) => (values.length ? sum(values) / values.length : 0)
/** A spread within floating-point noise of the mean is exactly zero: a constant fractional series must not look noisy. */
const isNoise = (spread: number, average: number) =>
    spread <= Math.max(Math.abs(average) * FLOATING_POINT_NOISE, FLOATING_POINT_NOISE)
const populationSd = (values: number[]) => {
    if (values.length < 2) return 0
    const average = mean(values)
    const spread = Math.sqrt(mean(values.map((value) => (value - average) ** 2)))
    return isNoise(spread, average) ? 0 : spread
}
const median = (values: number[]) => {
    const sorted = [...values].sort((a, b) => a - b)
    const middle = Math.floor(sorted.length / 2)
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2
}
const withoutGaps = (values: Array<number | null>): number[] =>
    values.filter((value): value is number => value !== null)
const isSteady = (significanceSd: number | null) =>
    significanceSd !== null && Math.abs(significanceSd) < STEADY_BELOW_SD

/**
 * A flat baseline has no scale to judge a change by, so only an exactly unchanged value is
 * significant (zero); any other change has no significance at all.
 */
const significanceAgainst = (change: number, scale: number): number | null =>
    scale === 0 ? (change === 0 ? 0 : null) : change / scale

/**
 * Counts: the change in the per-day average between two ranges, expressed in daily standard deviations of the
 * baseline. The ranges may differ in length (a month and the shorter month before it), so their totals are not
 * comparable day for day; their averages are. The percentage change stays total against total.
 */
export const trend = (current: number[], prior: number[], baseline: number[]): Trend => {
    const currentTotal = sum(current)
    const priorTotal = sum(prior)
    const deltaAbs = currentTotal - priorTotal
    const perDayChange = mean(current) - mean(prior)
    const significanceSd =
        current.length === 0 || prior.length === 0 ? null : significanceAgainst(perDayChange, populationSd(baseline))
    return {
        current: currentTotal,
        prior: priorTotal,
        deltaAbs,
        deltaPct: priorTotal === 0 ? null : deltaAbs / priorTotal,
        significanceSd,
        steady: isSteady(significanceSd),
    }
}

/** A ratio: recomputed from summed numerators and denominators, never averaged from daily ratios. */
export const rateTrend = (
    numCur: number,
    denCur: number,
    numPrior: number,
    denPrior: number,
    baselineRates: Array<number | null>,
): RateTrend => {
    const current = denCur === 0 ? null : numCur / denCur
    const prior = denPrior === 0 ? null : numPrior / denPrior
    const knownBaselineRates = withoutGaps(baselineRates)
    const significanceSd =
        current === null || prior === null
            ? null
            : significanceAgainst(current - prior, populationSd(knownBaselineRates))
    return {
        current,
        prior,
        deltaPts: current === null || prior === null ? null : (current - prior) * 100,
        significanceSd,
        steady: isSteady(significanceSd),
        baselineMean: knownBaselineRates.length ? mean(knownBaselineRates) : null,
    }
}

/** Days further than a multiple of the baseline's median absolute deviation from its median. */
export const outlierDays = (valuesByDate: Record<IsoDate, number>, baseline: number[]): IsoDate[] => {
    if (!baseline.length) return []
    const baselineMedian = median(baseline)
    const medianAbsoluteDeviation = median(baseline.map((value) => Math.abs(value - baselineMedian)))
    return Object.keys(valuesByDate)
        .sort()
        .filter((date) => {
            const value = valuesByDate[date]
            if (medianAbsoluteDeviation > 0)
                return Math.abs(value - baselineMedian) > OUTLIER_MAD_MULTIPLE * medianAbsoluteDeviation
            return value !== baselineMedian && baselineMedian > 0
        })
}

/**
 * A level such as subscribers: compare the end values; judge the per-day change (the change spread over the days
 * the range spans) against the baseline's own per-day changes. The end values must already be comparable, taken
 * over the same domains (see `levelPairOn` and `dailyLevelChanges`).
 */
export const levelTrend = (
    currentLevel: number | null,
    priorLevel: number | null,
    baselineDailyChanges: number[],
    daysInRange: number,
): Trend => {
    if (currentLevel === null || priorLevel === null) {
        return {
            current: currentLevel ?? 0,
            prior: priorLevel ?? 0,
            deltaAbs: 0,
            deltaPct: null,
            significanceSd: null,
            steady: false,
        }
    }
    const deltaAbs = currentLevel - priorLevel
    const significanceSd = significanceAgainst(deltaAbs / Math.max(daysInRange, 1), populationSd(baselineDailyChanges))
    return {
        current: currentLevel,
        prior: priorLevel,
        deltaAbs,
        deltaPct: priorLevel === 0 ? null : deltaAbs / priorLevel,
        significanceSd,
        steady: isSteady(significanceSd),
    }
}

/** Where a rate sits against its own baseline: percent above or below the mean, and whether that is at least one SD. */
export const contextFor = (
    rateCurrent: number | null,
    baselineRates: Array<number | null>,
): { pct: number | null; significant: boolean } => {
    const knownBaselineRates = withoutGaps(baselineRates)
    if (rateCurrent === null || !knownBaselineRates.length) return { pct: null, significant: false }
    const baselineMean = mean(knownBaselineRates)
    if (baselineMean === 0) return { pct: null, significant: false }
    const baselineSd = populationSd(knownBaselineRates)
    const distance = Math.abs(rateCurrent - baselineMean)
    return {
        pct: (rateCurrent - baselineMean) / baselineMean,
        significant: baselineSd === 0 ? !isNoise(distance, baselineMean) : distance >= baselineSd,
    }
}
